/**
 * =============================================================================
 * IDS CRM Web - Search the spare parts list (spare parts, Push 2)
 * =============================================================================
 *
 * The box engineers type a part into, in job Log Work, the ticket's work log
 * window and Add a part on the job page (build spec 9.4). It searches the
 * phone's copy of the list (utils/sparePartsList.js), so it works with no
 * signal, and always offers "Part not in the list" for what was typed, which
 * the office prices later.
 *
 * A SearchSelect combobox with the three phone props:
 *  - clearOnSelect: empty again after each pick, ready for the next part;
 *  - inlineResults: results in the page flow, above the keyboard, with the
 *    Item Number under each name (9 names repeat in the list);
 *  - keepOpenOnBlur: hiding the iPhone keyboard keeps the search.
 *
 * Enter never submits the surrounding form. Never put this, or a quantity
 * box, inside .form-group: AdminSettings.css sets inputs there to 14px, and
 * iPhones zoom on anything under 16px.
 * =============================================================================
 */

import React, { useCallback } from 'react';
import SearchSelect from './SearchSelect';
import { searchSpareParts } from '../utils/sparePartsList';
import './SparePartsPicker.css';

export const NOT_IN_LIST_ID = '__not_in_list__';

const STATUS_TEXT = {
  none: 'The parts list is not on this phone yet. Type the part\'s name and choose Part not in the list, or search again when you have signal.',
  error: 'The parts list could not be loaded. Type the part\'s name and choose Part not in the list, or search again shortly.',
  loading: 'Loading the parts list…'
};

const optionId = (row) => row.id;
const optionLabel = (row) => (row.notInList ? `Part not in the list: “${row.name}”` : row.name);
const optionSub = (row) => (row.notInList ? '' : row.number);

/**
 * @param {object} props
 * @param {string} props.id - the input's id, for a <label htmlFor>
 * @param {Array<{id, number, name}>} props.items - the list (stable until it changes)
 * @param {'ready'|'loading'|'none'|'error'} props.status
 * @param {boolean} [props.disabled]
 * @param {(row: {id, number, name}|{id, name, notInList: true}) => void} props.onPick
 */
function SparePartsPicker({ id, items, status = 'ready', disabled = false, onPick }) {
  // Memoised on items, which stays the same array until the list changes:
  // SearchSelect searches again whenever fetchOptions changes.
  const fetchOptions = useCallback((term) => {
    const found = searchSpareParts(items, term);
    const typed = String(term ?? '').trim();
    return typed.length >= 2 ? [...found, { id: NOT_IN_LIST_ID, name: typed, notInList: true }] : found;
  }, [items]);

  const handleSelect = useCallback((row) => {
    // The clear button sends null: nothing was picked.
    if (row && typeof onPick === 'function') onPick(row);
  }, [onPick]);

  const handleKeyDown = (event) => {
    // Enter picks the highlighted part (SearchSelect) and never saves Log Work.
    if (event.key === 'Enter') event.preventDefault();
  };

  const statusText = STATUS_TEXT[status] && !(status === 'loading' && items && items.length > 0) ? STATUS_TEXT[status] : null;

  return (
    <div className="spare-parts-picker" onKeyDown={handleKeyDown}>
      <SearchSelect
        id={id}
        variant="combobox"
        minChars={2}
        debounceMs={0}
        clearOnSelect
        inlineResults
        keepOpenOnBlur
        clearable
        selectedIndicator={null}
        placeholder="Search by name or Item Number"
        disabled={disabled}
        fetchOptions={fetchOptions}
        getOptionId={optionId}
        getOptionLabel={optionLabel}
        getOptionSub={optionSub}
        onSelect={handleSelect}
      />
      {statusText && <p className="spare-parts-picker-status">{statusText}</p>}
    </div>
  );
}

export default SparePartsPicker;
